import { Alert, Button, Card, Col, DatePicker, Empty, Row, Space, Statistic, Tag } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Board = {
  unmarked: unknown[];
  unmarked_periods?: unknown[];
  corrections: unknown[];
  unexplained: unknown[];
  repeated: unknown[];
  threshold_configured: boolean;
};

export default function AttendanceOverviewPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') || dayjs().format('YYYY-MM-DD');
  const query = useQuery({
    queryKey: ['education-attendance-overview', date],
    enabled: Boolean(date),
    queryFn: () => api.get<Board>('/education/attendance/overview', { params: { attendance_date: date } }),
  });
  const board = query.data?.data;
  const unmarked = board?.unmarked.length ?? 0;
  const unmarkedPeriods = board?.unmarked_periods?.length ?? 0;
  const corrections = board?.corrections.length ?? 0;
  const unexplained = board?.unexplained.length ?? 0;
  const repeated = board?.threshold_configured ? (board.repeated.length ?? 0) : null;

  return (
    <EducationPageChrome
      title={t('attendanceOverview')}
      description={t('attendanceOverviewIntro')}
      tags={<Tag>{date}</Tag>}
      extra={
        <Space wrap>
          <Button onClick={() => navigate('/education/attendance/settings')}>{t('attendanceSettings')}</Button>
          <Button onClick={() => navigate(`/education/attendance?${params.toString()}`)}>
            {t('backToAttendance')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Space wrap>
            <DatePicker
              value={dayjs(date)}
              format="DD-MMM-YYYY"
              onChange={(value) => {
                const next = new URLSearchParams(params);
                next.set('date', value ? value.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'));
                setParams(next);
              }}
            />
            <Button onClick={() => navigate('/education/attendance/exceptions?date=' + date)}>
              {t('attendanceExceptions')}
            </Button>
            <Button type="primary" onClick={() => navigate('/education/attendance/take')}>
              {t('takeAttendance')}
            </Button>
          </Space>
        </Card>
        <Alert
          type="info"
          showIcon
          message={t('overviewHint', {
            defaultValue:
              'Unmarked sections need a daily register. Unmarked periods follow the published timetable. Pending corrections wait on approval. Fees and exam results are not shown here.',
          })}
        />
        <Card size="small" loading={query.isLoading}>
          {!board && !query.isLoading ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('attendanceEmpty', { defaultValue: 'No overview data' })} />
          ) : (
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Statistic title={t('unmarked')} value={unmarked} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Statistic title={t('unmarkedPeriods', { defaultValue: 'Unmarked periods' })} value={unmarkedPeriods} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Statistic title={t('pendingCorrections')} value={corrections} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={5}>
                <Statistic title={t('unexplainedAbsences')} value={unexplained} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={5}>
                <Statistic
                  title={t('repeatedAbsences')}
                  value={repeated ?? t('thresholdNotConfigured')}
                  valueStyle={repeated == null ? { fontSize: 16 } : undefined}
                />
              </Col>
            </Row>
          )}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
